import { useRouter } from 'expo-router';
import { Paragraph, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { AuthenticatedImage } from '@/components/authenticated-image';
import { Badge } from '@/components/badge';
import { Card } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { Heading } from '@/components/heading';
import { ListExplainer } from '@/components/list-explainer';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { ResourceListView } from '@/components/resource-list-view';
import { RiseIn } from '@/components/rise-in';
import { ScreenContainer } from '@/components/screen-container';
import { useDockInset, useDockScroll } from '@/lib/dock';
import { WebRefreshButton } from '@/components/web-refresh';
import { useConnections } from '@/hooks/use-connections';
import { usePaginatedResource } from '@/hooks/use-paginated-resource';
import { useProfile } from '@/hooks/use-profile';
import { type Connection } from '@/lib/api';
import { displayHandle } from '@/lib/avatar-identity';
import { deriveUnreadLabel, messagePreview } from '@/lib/conversation';
import { selectAllConnections } from '@/lib/connections-cache';
import { isSparseList } from '@/lib/list-state';
import { relativeTime } from '@/lib/relative-time';
import { space } from '@/theme/tokens.generated';

// Issue #89's "Matches" tab -- a signed-in user's mutual Connections
// (built by #7/#8's reveal-request/connection flow, listed here via
// #137/#138's paginated GET /me/connections). Mirrors threads/index.tsx's
// exact FlatList + pull-to-refresh + empty/error/pending-state
// conventions, rather than the complete-list read (useAllConnections)
// the reveal-requests screen uses -- that screen needs EVERY page to
// compute counts/badges across several derived sections, this one is a
// single paginated list with no cross-page computation, the same shape
// as the thread list. Both hooks read the ONE ['me','connections']
// cache; see hooks/use-connections.ts.
export function Matches() {
  const dockInset = useDockInset();
  const dockScroll = useDockScroll();
  const router = useRouter();

  const connectionsQuery = useConnections();
  // The list machine (states, refresh haptic, onEndReached double-fire
  // guard) lives in usePaginatedResource / ResourceListView; this
  // screen keeps only its header, its card, and its empty state.
  const matches = usePaginatedResource(connectionsQuery, selectAllConnections);

  return (
    <ScreenContainer scrollUnderDock>
      <YStack flex={1}>
        {/* Refresh moved inline next to the heading (issue #290) -- it
            used to be its own centered row ABOVE this one, sitting
            above the "Matches" H1 itself. */}
        <XStack pb="$3" alignItems="flex-start" justifyContent="space-between">
          <YStack gap="$1" flex={1}>
            <Heading>Matches</Heading>
            <Paragraph color="$colorSecondary">
              People you and a neighbor both agreed to reveal identities with.
            </Paragraph>
          </YStack>
          <XStack pt="$1">
            <WebRefreshButton onRefresh={() => matches.refetch()} refreshing={matches.isRefetching} />
          </XStack>
        </XStack>

        {/* Pending, error-without-data, stale-notice, footer, and
            refresh-control all render via the shared view; this screen
            supplies only the item render, the empty state, and the
            empty-case contentContainerStyle. */}
        <ResourceListView
          resource={matches}
          errorTitle="Couldn't load matches"
          renderItem={({ item, index }) => (
            <RiseIn index={index}>
              <MatchCard
                match={item}
                onPress={() =>
                  router.push({
                    // Bare form, like every other router.push in this
                    // repo -- the group segment is a filesystem detail,
                    // not part of the route's address.
                    pathname: '/messages/[connectionId]',
                    params: { connectionId: item.id },
                  })
                }
              />
            </RiseIn>
          )}
          // Moved into ListEmptyComponent, not a sibling branch --
          // same fix as threads' EmptyThreadsList: a plain YStack
          // outside the FlatList has no scrollable ancestor, so the
          // empty state couldn't be pulled to refresh at all.
          // flexGrow:1 below (only when empty) lets this state fill
          // the whole remaining viewport instead of floating as a
          // small centered block under the header -- this app's own
          // composition rule ("an empty state owns the viewport it
          // is in").
          emptyNode={<EmptyMatchesList />}
          endNote="That's everyone you've matched with. More will turn up."
          // Decision 4 (issue #563): one or two matches leave most of the
          // screen empty below them -- the explainer says what fills this
          // list and what to do about it. Zero stays the empty state's
          // job (isSparseList's own doc comment).
          footer={
            isSparseList(matches.items.length, 3) ? (
              <YStack pt="$3">
                <ListExplainer
                  title="How matches happen"
                  body="A match is you and a neighbor both agreeing to reveal who you are. Chat on a nearby thread and matches follow."
                />
              </YStack>
            ) : undefined
          }
          listProps={{
            style: { flex: 1 },
            ...dockScroll,
            keyExtractor: (match) => match.id,
            contentContainerStyle:
              matches.items.length === 0
                ? { flexGrow: 1, paddingBottom: space[6] + dockInset }
                : { gap: space[2], paddingBottom: space[6] + dockInset },
          }}
        />
      </YStack>
    </ScreenContainer>
  );
}

// Issue #292: the previous version explained twice (two full paragraphs,
// one of them literally instructing "Start a conversation on a nearby
// thread" with no way to actually do that) and never acted -- this
// app's own empty-state formula (brand-and-operations.md) is "explain
// the value -> guide to first action," not "explain the value twice."
// Cut to two lines, each under the 45-75ch measure rule
// (typography-and-spacing.md), plus a real "Browse nearby threads"
// button (router.push('/'), the Threads tab) -- the missing second half
// of the formula this empty state never had.
function EmptyMatchesList() {
  const router = useRouter();

  return (
    <EmptyState
      ring
      title="No matches yet"
      description={[
        'A match is when you and a neighbor both agree to reveal identities.',
        'Start a conversation on a nearby thread to get one going.',
      ]}
      action={{ label: 'Browse nearby threads', onPress: () => router.push('/') }}
    />
  );
}

// The fetch-next-page footer moved to the shared ResourceListFooter
// (components/resource-list-view.tsx): same shape this file's own
// MatchesListFooter used to have -- a later-page failure states inline
// with a retry and never clears the already-rendered pages.

// Issue #64: the row used to open the counterpart's profile, which left
// the reveal moment with no destination -- a match existed and there was
// nothing to do with it. It now opens the conversation, and the profile
// is one tap further on from the chat header. The preview line is the
// row's differentiator (components.md's own "a long list needs one
// differentiator"): without it every match reads identically, which is
// exactly what this list looked like before.
//
// Founder's own adversarial-lens critique, 2026-09-16: "the Matches
// screen shows no photos... the entire payoff of matching (seeing who
// they are) isn't front-and-center on the one screen literally named
// for it." Real bug, not a taste call -- a mutual Connection already
// unlocks this person's real photos (GetProfileHandler's photo gate;
// profile-view.tsx already renders them), but this card was still
// falling back to the SAME pseudonymous letter-avatar an anonymous
// thread post gets. `Connection` itself (api-types.ts) carries no
// photo field, so this fetches the match's own profile per-row --
// the exact same pattern reveal-requests.tsx's IncomingRequestRow
// already uses for identity context at this list's scale (that row's
// own doc comment: "One query per card is fine at this screen's scale
// ... TanStack dedupes by key with the profile view's own query"),
// and the SAME ['profile', userId] query key, so a match already
// viewed via profile-view.tsx (or an incoming request row) hits a warm
// cache instead of a second network round trip.
function MatchCard({ match, onPress }: { match: Connection; onPress: () => void }) {
  const profile = useProfile(match.otherUserId);
  const photoPath = profile.data?.photos[0];

  const unreadLabel = deriveUnreadLabel(match.unreadCount);
  // lastMessage is optional AND nullable (api-types.ts) -- absent and
  // explicitly null both mean "no messages yet", and this truthiness
  // test is the one form that covers both.
  const preview = match.lastMessage ? messagePreview(match.lastMessage.body) : undefined;

  return (
    // Issue #296, global density pass -- same p="$3" tightening as
    // ThreadCard/PostCard, found "along the way" per that issue's own
    // scope note (identical Card p="$4" pattern).
    <Card
      onPress={onPress}
      p="$3"
      gap="$1"
      accessibilityLabel={`Open conversation with ${displayHandle(match.otherUserHandle)}`}
    >
      <XStack alignItems="center" gap="$3">
        {photoPath ? (
          <AuthenticatedImage
            path={photoPath}
            accessibilityLabel={`${displayHandle(match.otherUserHandle)}'s photo`}
            width={48}
            aspectRatio={1}
            objectFit="cover"
            borderRadius="$full"
          />
        ) : (
          <PseudonymousAvatar handle={match.otherUserHandle} id={match.otherUserId} size={48} />
        )}
        <YStack flexShrink={1} flex={1} gap="$1">
          <XStack alignItems="center" gap="$2">
            {/* flexShrink + numberOfLines -- handle has no length limit
                anywhere in the stack, same reasoning as ThreadCard's own
                creator-handle text a few files over. */}
            <Text
              fontWeight="$semibold"
              color="$color"
              fontSize="$titleS"
              lineHeight="$titleS"
              textAlign="left"
              flexShrink={1}
              numberOfLines={1}
            >
              {displayHandle(match.otherUserHandle)}
            </Text>
            {/* A real status word, never a bare colored dot -- the same
                no-color-only rule Badge's own doc comment states. */}
            {unreadLabel && (
              <Badge variant="primary" paddingHorizontal="$2" paddingVertical="$0_5">
                {unreadLabel}
              </Badge>
            )}
          </XStack>
          {/* A match with no messages yet gets an invitation to write the
              first one rather than a blank line -- a row with an empty
              middle reads as a row that failed to load. */}
          <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta" textAlign="left" numberOfLines={1}>
            {preview ?? 'Say hello'}
          </Text>
          {/* The message's own time once there is one, the match's time
              until then -- a conversation row dated by the match reads as
              stale the moment anyone replies. */}
          <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" textAlign="left">
            {match.lastMessage ? relativeTime(match.lastMessage.createdAt) : `Matched ${relativeTime(match.createdAt)}`}
          </Text>
        </YStack>
      </XStack>
    </Card>
  );
}
